import { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // État pour les invitations
    const [invitEmail, setInvitEmail] = useState('');
    const [invitMessage, setInvitMessage] = useState('');
    const [invitError, setInvitError] = useState('');
    const [invitLink, setInvitLink] = useState('');
    const [invitLoading, setInvitLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // État pour la liste des invitations actives
    const [invitations, setInvitations] = useState([]);
    const [invitationsLoading, setInvitationsLoading] = useState(true);
    const [revoking, setRevoking] = useState(null);

    const fetchInvitations = async () => {
        try {
            const { data } = await apiClient.get('/invitations/admin');
            setInvitations(data);
            setInvitationsLoading(false);
        } catch (err) {
            console.error(err);
            setInvitationsLoading(false);
        }
    };

    const handleRevoke = async (id) => {
        setRevoking(id);
        try {
            await apiClient.delete(`/invitations/admin/${id}`);
            setInvitations(invitations.filter(inv => inv._id !== id));
        } catch (err) {
            console.error(err);
        } finally {
            setRevoking(null);
        }
    };

    const fetchUsers = async () => {
        try {
            const { data } = await apiClient.get('/users');
            setUsers(data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchInvitations();
    }, []);

    return (
        <div className="admin-page page-transition">
            <h1>Administration</h1>

            {/* Invitation Section */}
            <div className="admin-card">
                <h3>Envoyer une invitation</h3>
                <p className="card-subtitle">Envoyez un lien d'inscription à un client (il choisira son propre mot de passe).</p>

                {invitMessage && <div className="popup-notification success" style={{ position: 'static', transform: 'none', marginBottom: '20px' }}><span>&#9989;</span>{invitMessage}</div>}
                {invitError && <div className="popup-notification error" style={{ position: 'static', transform: 'none', marginBottom: '20px' }}><span>&#9888;&#65039;</span>{invitError}</div>}

                <form onSubmit={async (e) => {
                    e.preventDefault();
                    setInvitMessage('');
                    setInvitError('');
                    setInvitLink('');
                    setCopied(false);
                    setInvitLoading(true);

                    try {
                        const { data } = await apiClient.post('/invitations/admin', { email: invitEmail });
                        setInvitMessage(data.message);
                        setInvitLink(data.registrationLink);
                        setInvitEmail('');
                        fetchInvitations();
                    } catch (err) {
                        setInvitError(err.response?.data?.message || 'Erreur lors de la création de l\'invitation');
                    } finally {
                        setInvitLoading(false);
                    }
                }} className="profile-form">
                    <div className="input-group">
                        <label className="input-label">Email du client</label>
                        <input
                            className="input-field"
                            type="email"
                            value={invitEmail}
                            onChange={(e) => setInvitEmail(e.target.value)}
                            placeholder="Email du client"
                            required
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" style={{ marginTop: '20px', width: '100%' }} disabled={invitLoading}>
                        {invitLoading ? 'Création...' : 'Créer l\'invitation'}
                    </button>
                </form>

                {invitLink && (
                    <div className="admin-link-box">
                        <label className="input-label" style={{ marginBottom: '8px', display: 'block' }}>Lien d'inscription (valide 7 jours) :</label>
                        <code style={{ display: 'block', fontSize: '13px', color: 'var(--primary-color)', marginBottom: '10px', wordBreak: 'break-all' }}>{invitLink}</code>
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ padding: '8px 16px', fontSize: '13px', width: '100%' }}
                            onClick={() => {
                                navigator.clipboard.writeText(invitLink);
                                setCopied(true);
                                setTimeout(() => setCopied(false), 3000);
                            }}
                        >
                            {copied ? 'Copié !' : 'Copier le lien'}
                        </button>
                    </div>
                )}
            </div>

            {/* Active Invitations Section */}
            <div className="admin-card">
                <h3>Invitations actives</h3>
                <p className="card-subtitle">Invitations en attente d'utilisation.</p>

                {/* Desktop table */}
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Expire le</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invitationsLoading ? (
                                <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center' }}>Chargement...</td></tr>
                            ) : invitations.length === 0 ? (
                                <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center' }}>Aucune invitation active</td></tr>
                            ) : (
                                invitations.map((inv) => (
                                    <tr key={inv._id}>
                                        <td>{inv.email}</td>
                                        <td>{new Date(inv.expiresAt).toLocaleDateString('fr-FR')}</td>
                                        <td>
                                            <button
                                                className="btn-revoke"
                                                disabled={revoking === inv._id}
                                                onClick={() => handleRevoke(inv._id)}
                                                style={{ opacity: revoking === inv._id ? 0.6 : 1 }}
                                            >
                                                {revoking === inv._id ? 'Révocation...' : 'Révoquer'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="admin-mobile-list">
                    {invitationsLoading ? (
                        <p style={{ textAlign: 'center', padding: '20px' }}>Chargement...</p>
                    ) : invitations.length === 0 ? (
                        <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Aucune invitation active</p>
                    ) : (
                        invitations.map((inv) => (
                            <div key={inv._id} className="admin-mobile-item">
                                <div className="admin-mobile-field">
                                    <span className="admin-mobile-label">Email</span>
                                    <span className="admin-mobile-value">{inv.email}</span>
                                </div>
                                <div className="admin-mobile-field">
                                    <span className="admin-mobile-label">Expire le</span>
                                    <span className="admin-mobile-value">{new Date(inv.expiresAt).toLocaleDateString('fr-FR')}</span>
                                </div>
                                <button
                                    className="btn-revoke"
                                    style={{ width: '100%', marginTop: '8px', opacity: revoking === inv._id ? 0.6 : 1 }}
                                    disabled={revoking === inv._id}
                                    onClick={() => handleRevoke(inv._id)}
                                >
                                    {revoking === inv._id ? 'Révocation...' : 'Révoquer'}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* User List Section */}
            <div className="admin-card">
                <h3>Liste des utilisateurs</h3>

                {/* Desktop table */}
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th>Email</th>
                                <th>Rôle</th>
                                <th>Date de création</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center' }}>Chargement...</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center' }}>Aucun utilisateur trouvé</td></tr>
                            ) : (
                                users.map((u) => (
                                    <tr key={u._id}>
                                        <td>{u.name}</td>
                                        <td>{u.email}</td>
                                        <td>
                                            <span className={`admin-role-badge ${u.role === 'admin' ? 'admin' : ''}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td>{new Date(u.createdAt || Date.now()).toLocaleDateString('fr-FR')}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="admin-mobile-list">
                    {loading ? (
                        <p style={{ textAlign: 'center', padding: '20px' }}>Chargement...</p>
                    ) : users.length === 0 ? (
                        <p style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Aucun utilisateur trouvé</p>
                    ) : (
                        users.map((u) => (
                            <div key={u._id} className="admin-mobile-item">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <strong>{u.name}</strong>
                                    <span className={`admin-role-badge ${u.role === 'admin' ? 'admin' : ''}`}>
                                        {u.role}
                                    </span>
                                </div>
                                <div className="admin-mobile-field">
                                    <span className="admin-mobile-label">Email</span>
                                    <span className="admin-mobile-value">{u.email}</span>
                                </div>
                                <div className="admin-mobile-field">
                                    <span className="admin-mobile-label">Inscrit le</span>
                                    <span className="admin-mobile-value">{new Date(u.createdAt || Date.now()).toLocaleDateString('fr-FR')}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
